export interface MuteRpcResult {
  muted: boolean;
}

export interface MuteControllerState {
  muted: boolean;
  updating: boolean;
  error: string | null;
}

export interface MuteControllerOptions {
  initialMuted: boolean;
  sendRpc: (muted: boolean) => Promise<MuteRpcResult | null | undefined>;
  onChange?: (state: MuteControllerState) => void;
}

/**
 * Dealer mute state machine:
 *   - Before the transport is ready, setMute queues a pending value and tracks state optimistically.
 *   - Once ready, the pending value is flushed via sendRpc.
 *   - When ready, setMute applies optimistically and reverts on RPC failure — but the
 *     DESIRED value survives the failure, and `resync()` re-sends it once the transport
 *     is back (#478: a foreground un-mute that raced livekit's reconnect timed out and
 *     the dealer stayed muted for the session).
 *   - The RPC response's muted value is authoritative and replaces the optimistic state.
 *
 * The hook layer owns the transport (room.localParticipant.performRpc) and React state sync.
 */
export class MuteController {
  private muted: boolean;
  /** What the player (or host) last asked for — the value a resync re-sends. */
  private desired: boolean;
  /** `desired` was never confirmed by the agent (the last RPC for it failed). */
  private dirty = false;
  /** A resync arrived while an RPC was in flight; re-checked when it settles. */
  private resyncRequested = false;
  private pending: boolean | null = null;
  private ready = false;
  private updating = false;
  private lastError: string | null = null;
  private destroyed = false;
  private readonly options: MuteControllerOptions;

  constructor(options: MuteControllerOptions) {
    this.options = options;
    this.muted = options.initialMuted;
    this.desired = options.initialMuted;
  }

  async setMute(muted: boolean): Promise<void> {
    if (this.destroyed) return;
    this.desired = muted;

    if (!this.ready) {
      this.pending = muted;
      if (this.muted !== muted) {
        this.muted = muted;
        this.notify();
      }
      return;
    }

    const previous = this.muted;
    this.updating = true;
    this.lastError = null;

    if (this.muted !== muted) {
      this.muted = muted;
    }
    this.notify();

    try {
      const result = await this.options.sendRpc(muted);
      if (this.destroyed) return;
      const resolved = typeof result?.muted === 'boolean' ? result.muted : muted;
      this.muted = resolved;
      this.dirty = false;
    } catch (err) {
      if (this.destroyed) return;
      this.lastError = err instanceof Error ? err.message : 'Unknown error';
      this.muted = previous; // honest: the agent did not get it
      this.dirty = true; // ...but remember what was wanted, for resync()
    } finally {
      if (!this.destroyed) {
        this.updating = false;
        this.notify();
        if (this.resyncRequested) {
          this.resyncRequested = false;
          void this.resync(); // no-op unless this RPC just failed (dirty)
        }
      }
    }
  }

  setReady(ready: boolean): void {
    const wasReady = this.ready;
    this.ready = ready;
    if (!ready || wasReady) return;
    if (this.pending !== null) {
      const desired = this.pending;
      this.pending = null;
      void this.setMute(desired);
      return;
    }
    void this.resync();
  }

  /**
   * Re-send the desired state if the agent never confirmed it (a failed RPC
   * across a transport drop). Called when the transport is back; a no-op when
   * nothing is owed. While an RPC is in flight the request is LATCHED, not
   * dropped — livekit's resume can land mid-RPC (RoomEvent.Reconnected fires
   * while the toggle's RPC is still pending), and its failure would otherwise
   * be the last word — and setMute's `finally` re-enters once it settles.
   */
  async resync(): Promise<void> {
    if (this.destroyed || !this.ready) return;
    if (this.updating) {
      this.resyncRequested = true;
      return;
    }
    if (!this.dirty) return;
    await this.setMute(this.desired);
  }

  /**
   * The player (or host) asked for a value the agent has not confirmed —
   * queued pre-ready, carried by an in-flight RPC, or left over from a failed
   * one. While this is true, connect()'s reconciliation against the server's
   * dealer-mute flag must stand down: the agent stamps that flag only on a
   * SUCCESSFUL set_mute, so across a failed toggle it is exactly the stale
   * value the resync is correcting — sending it would reverse the player's
   * action and overwrite `desired` (#482 review).
   */
  hasPendingDesire(): boolean {
    return this.pending !== null || this.updating || this.dirty;
  }

  getState(): MuteControllerState {
    return { muted: this.muted, updating: this.updating, error: this.lastError };
  }

  destroy(): void {
    this.destroyed = true;
  }

  private notify(): void {
    this.options.onChange?.(this.getState());
  }
}
